import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideAnimations } from '@angular/platform-browser/animations';

import { provideToastr } from 'ngx-toastr';
import { appRoutes } from './app.routes';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { authReducer } from './state/store/auth/auth.reducer';
import { AuthEffects } from './state/store/auth/auth.effects';
import { ParentEffects } from './state/store/parent/parent.effects';
import { parentReducer } from './state/store/parent/parent.reducer';
import { clientReducer } from './state/store/client/client.reducer';
import { ClientEffects } from './state/store/client/client.effects';
import { ProgramEffects } from './state/store/program/program.effects';
import { programReducer } from './state/store/program/program.reducer';
import { locationReducer } from './state/store/location/location.reducer';
import { LocationEffects } from './state/store/location/location.effects';
import { FilterEffects } from './state/store/filter/filter.effects';
import { filterReducer } from './state/store/filter/filter.reducer';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { RolePermissionEffects } from './state/store/roles/role-permission.effects';
import { rolePermissionReducer } from './state/store/roles/role-permission.reducer';
import { userReducer } from './state/store/user/user.reducer';
import { UserEffects } from './state/store/user/user.effects';
import { environment } from 'src/environments/environment';
import { fileUploadReducer } from './state/store/file-upload/file-upload.reducer';
import { FileUploadEffects } from './state/store/file-upload/file-upload.effects';
import { roleReducer } from './state/store/role/role.reducer';
import { RoleEffects } from './state/store/role/role.effects';
import { dispositionReducer } from './state/store/disposition/disposition.reducer';
import { DispositionEffects } from './state/store/disposition/disposition.effects';
import { APP_INIT_PROVIDER } from './core/initializers/app.initializer';

// Factory function for the loader
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    provideRouter(appRoutes),
    importProvidersFrom(
      NgbModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideStore({
      auth: authReducer,
      client: clientReducer,
      location: locationReducer,
      disposition: dispositionReducer,
      parent: parentReducer,
      program: programReducer,
      rolePermission: rolePermissionReducer,
      filter: filterReducer,
      user: userReducer,
      fileUpload: fileUploadReducer,
      role: roleReducer,
    }),
    provideEffects([
      AuthEffects,
      ClientEffects,
      LocationEffects,
      ParentEffects,
      ProgramEffects,
      RolePermissionEffects,
      FilterEffects,
      UserEffects,
      FileUploadEffects,
      RoleEffects,
      DispositionEffects
    ]),
    APP_INIT_PROVIDER,
    provideStoreDevtools({
      maxAge: 25,
      trace: true,
      logOnly: environment.production,
    }),
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-bottom-full-width',
      timeOut: 5000,
      extendedTimeOut: 1000,
      maxOpened:1
    }),
  ],
};
